import { useState, useCallback } from 'react';
import { Upload, FileText, X, Files, Sparkles } from 'lucide-react';
import { AnalysisTicker } from './AnalysisTicker';
import { KnowledgeReviewList } from './KnowledgeReviewList';
import { OnboardingStepHeader } from './OnboardingStepHeader';
import { OnboardingFooter } from './OnboardingFooter';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { useOrganization } from '@/lib/organization-context';
import { errorToast, successToast } from '@/components/ui/sonner';
import { triggerEmbeddingBatch } from '@/lib/embedding-utils';
// sample content is now seeded automatically on signup; no client call needed
import type { ParsedLibraryItem } from '@/lib/uploadConstants';

interface KnowledgeDepositProps {
  onComplete: (extractedCount: number) => void;
  onSkip: () => void;
  onBack?: () => void;
}

interface ExtractionResults {
  items_created?: number;
  extracted_count?: number;
  categories?: {
    qa?: number;
    case_studies?: number;
    team_members?: number;
    company_sections?: number;
  };
  duplicates_found?: number;
  files_processed?: number;
  total_pages?: number;
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/csv',
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
const MAX_FILES = 10;
const MAX_TOTAL_SIZE = MAX_FILES * 20 * 1024 * 1024;

type Stage = 'upload' | 'processing' | 'review';

export function KnowledgeDeposit({ onComplete, onSkip }: Omit<KnowledgeDepositProps, 'onBack'>) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedCount, setExtractedCount] = useState(0);
  const [extractionResults, setExtractionResults] = useState<ExtractionResults | null>(null);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [stage, setStage] = useState<Stage>('upload');
  const [parsedItems, setParsedItems] = useState<ParsedLibraryItem[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);

  const validateFile = (file: File): boolean => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_TYPES.includes(file.type)) {
      errorToast('Invalid file type', `${file.name}: Please upload PDF, DOCX, TXT, MD, or CSV files.`);
      return false;
    }
    if (file.size > 20 * 1024 * 1024) {
      errorToast('File too large', `${file.name}: Maximum file size is 20MB per file.`);
      return false;
    }
    return true;
  };

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => { const result = reader.result as string; resolve(result.split(',')[1]); };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const processFiles = async (files: File[]) => {
    if (!user || files.length === 0) return;
    setIsProcessing(true);
    setStage('processing');
    setExtractionResults(null);
    setCurrentFileIndex(0);
    setParsedItems([]);

    try {
      const fileDataPromises = files.map(async (file, index) => {
        setCurrentFileIndex(index);
        const base64Content = await readFileAsBase64(file);
        return { name: file.name, content: base64Content, type: file.type };
      });
      const fileData = await Promise.all(fileDataPromises);
      const { data, error } = await supabase.functions.invoke('parse-library-upload', { body: { files: fileData } });
      if (error) throw error;

      const items: ParsedLibraryItem[] = data?.items || [];
      setExtractedCount(items.length);
      const categories = {
        qa: items.filter(i => i.suggested_category === 'qa').length,
        case_studies: items.filter(i => i.suggested_category === 'case-study').length,
        team_members: items.filter(i => i.suggested_category === 'team').length,
        company_sections: items.filter(i => i.suggested_category === 'company-content').length,
      };
      setExtractionResults({ extracted_count: items.length, categories, files_processed: files.length });
      setParsedItems(items);
    } catch (error) {
      console.error('Error processing files:', error);
      errorToast('Processing failed', error instanceof Error ? error.message : 'Failed to process files.');
      setIsProcessing(false);
      setStage('upload');
      setSelectedFiles([]);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(validateFile);
    if (validFiles.length > 0) {
      const newFiles = [...selectedFiles, ...validFiles].slice(0, MAX_FILES);
      const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) { errorToast('File exceeds the 20MB limit', 'Please compress or split the document.'); return; }
      setSelectedFiles(newFiles);
      if (selectedFiles.length === 0) processFiles(newFiles);
    }
  }, [selectedFiles]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(validateFile);
    if (validFiles.length > 0) {
      const newFiles = [...selectedFiles, ...validFiles].slice(0, MAX_FILES);
      const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) { errorToast('File exceeds the 20MB limit', 'Please compress or split the document.'); return; }
      setSelectedFiles(newFiles);
      if (selectedFiles.length === 0) processFiles(newFiles);
    }
  };

  const handleTickerComplete = () => {
    setIsProcessing(false);
    if (parsedItems.length > 0) {
      setStage('review');
    } else {
      errorToast('No content extracted', 'No recognizable content was found in the uploaded files.');
      setStage('upload');
      setSelectedFiles([]);
    }
  };

  const handleImportSelected = async (selectedItems: ParsedLibraryItem[]) => {
    if (!user?.id || !organization?.id || selectedItems.length === 0) return;
    setIsImporting(true);
    try {
      let successCount = 0;
      const newItemsForEmbedding: Array<{ id: string; type: string }> = [];

      const qaItems = selectedItems.filter(i => i.suggested_category === 'qa');
      const companySectionItems = selectedItems.filter(i => i.suggested_category === 'company-content');
      const caseStudyItems = selectedItems.filter(i => i.suggested_category === 'case-study');
      const teamItems = selectedItems.filter(i => i.suggested_category === 'team');
      const pricingItems = selectedItems.filter(i => i.suggested_category === 'pricing');

      if (qaItems.length > 0) {
        const { data, error } = await supabase.from('content_library').insert(
          qaItems.map(item => ({ user_id: user.id, organization_id: organization.id, question: item.title, answer: item.body, category: item.universalCategory, tags: item.suggested_tags }))
        ).select('id');
        if (!error) { successCount += qaItems.length; data?.forEach(r => newItemsForEmbedding.push({ id: r.id, type: 'qa' })); }
      }
      if (companySectionItems.length > 0) {
        const { data, error } = await supabase.from('company_sections').insert(
          companySectionItems.map(item => ({ user_id: user.id, organization_id: organization.id, title: item.title, content: item.body, section_type: 'custom', category: item.universalCategory, tags: item.suggested_tags }))
        ).select('id');
        if (!error) { successCount += companySectionItems.length; data?.forEach(r => newItemsForEmbedding.push({ id: r.id, type: 'company_section' })); }
      }
      if (caseStudyItems.length > 0) {
        const { data, error } = await supabase.from('case_studies').insert(
          caseStudyItems.map(item => ({ user_id: user.id, organization_id: organization.id, title: item.title, content: item.body, category: item.universalCategory, tags: item.suggested_tags }))
        ).select('id');
        if (!error) { successCount += caseStudyItems.length; data?.forEach(r => newItemsForEmbedding.push({ id: r.id, type: 'case_study' })); }
      }
      if (pricingItems.length > 0) {
        const { error } = await supabase.from('pricing_templates').insert(
          pricingItems.map(item => ({ user_id: user.id, organization_id: organization.id, template_name: item.title, commercial_terms: item.body, category: item.universalCategory }))
        );
        if (!error) successCount += pricingItems.length;
      }
      if (teamItems.length > 0) {
        const { data, error } = await supabase.from('team_members' as any).insert(
          teamItems.map(item => ({ user_id: user.id, organization_id: organization.id, name: item.title, bio: item.body, category: item.universalCategory, expertise_tags: item.suggested_tags, is_active: true }))
        ).select('id');
        if (!error) { successCount += teamItems.length; (data as any)?.forEach((r: any) => newItemsForEmbedding.push({ id: r.id, type: 'team_member' })); }
      }

      if (newItemsForEmbedding.length > 0) triggerEmbeddingBatch(newItemsForEmbedding);
      if (successCount > 0) successToast(`${successCount} item${successCount > 1 ? 's' : ''} imported to your vault`);
      onComplete(successCount);
    } catch (error) {
      console.error('Import error:', error);
      errorToast('Import failed', 'Failed to import items. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    if (newFiles.length === 0) {
      setIsProcessing(false); setStage('upload'); setExtractedCount(0); setExtractionResults(null); setParsedItems([]);
    }
  };

  const totalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  const headlineTitle = stage === 'review' ? 'Review your extractions.' : 'Build your content library.';
  const headlineSubtitle = stage === 'review'
    ? `${parsedItems.length} items extracted — deselect any you don't want to import.`
    : 'Upload company documents and Sorvaine will extract reusable content. The more you add, the better your proposals.';

  return (
    <>
      <OnboardingStepHeader step={4} totalSteps={5} title={headlineTitle} subtitle={headlineSubtitle} />

      {/* === UPLOAD STAGE === */}
      {stage === 'upload' && (
        <>
          {/* Dropzone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `1.5px dashed ${isDragging ? 'rgba(194,84,26,0.4)' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: 8,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              background: isDragging ? 'rgba(194,84,26,0.02)' : 'transparent',
              position: 'relative',
              transition: 'all 150ms',
            }}
          >
            <input
              type="file"
              accept={ACCEPTED_EXTENSIONS.join(',')}
              onChange={handleFileSelect}
              multiple
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            <Upload className="w-8 h-8 mx-auto mb-2" style={{ color: isDragging ? '#C2541A' : 'rgba(255,255,255,0.25)' }} />
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
              Drop documents here or browse
            </p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
              PDF, DOCX, TXT, MD, CSV · Max 20MB · Up to 10 files
            </p>
          </div>

          {/* Hint box */}
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 6,
              padding: '12px 14px',
              marginTop: 12,
            }}
          >
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
              Good starting points: company overview, case studies, CVs of key staff, certifications, past proposals.
            </p>
          </div>

          {/* Sample content shortcut — every account is pre-seeded with a sample
              RFP and 8 vault items, so we just deep-link there. */}
          <button
            type="button"
            onClick={() => { onSkip(); window.location.assign('/library?tag=sample'); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              marginTop: 10,
              padding: '10px 14px',
              borderRadius: 6,
              border: '1px solid rgba(194,84,26,0.25)',
              background: 'rgba(194,84,26,0.04)',
              color: '#C2541A',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Show me the sample content already in my vault
          </button>

          <OnboardingFooter onSkip={onSkip} skipLabel="Add content later" disabled type="button" />
        </>
      )}

      {/* === PROCESSING STAGE === */}
      {stage === 'processing' && (
        <div
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <div className="flex items-center justify-between" style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center gap-2">
              <Files className="w-4 h-4" style={{ color: '#C2541A' }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: '#fff' }}>
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                ({(totalSize / 1024 / 1024).toFixed(1)} MB)
              </span>
            </div>
          </div>
          <div style={{ maxHeight: 120, overflowY: 'auto', padding: 10 }} className="space-y-1">
            {selectedFiles.map((file, index) => (
              <div key={`${file.name}-${index}`} className="flex items-center gap-2" style={{ padding: '8px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.03)' }}>
                <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: 'rgba(255,255,255,0.35)' }} />
                <span className="truncate flex-1" style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>{file.name}</span>
                <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
              </div>
            ))}
          </div>
          <AnalysisTicker
            isActive={isProcessing}
            onComplete={handleTickerComplete}
            extractedCount={extractedCount}
            categories={extractionResults?.categories}
            filesProcessed={extractionResults?.files_processed}
            duplicatesFound={extractionResults?.duplicates_found}
          />
        </div>
      )}

      {/* === REVIEW STAGE === */}
      {stage === 'review' && parsedItems.length > 0 && (
        <KnowledgeReviewList
          items={parsedItems}
          onImport={handleImportSelected}
          onSkip={onSkip}
          isImporting={isImporting}
        />
      )}
    </>
  );
}
